import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEBOUNCE_MS = 250;

export type PersistedLayout = number[];

type Options = {
  /** localStorage key. Pass a different key per layout mode so 2-pane and 3-pane don't bleed. */
  storageKey: string;
  /** Default percentages summing to ~100, e.g. [50, 50] or [25, 50, 25]. */
  defaultLayout: PersistedLayout;
  /** Per-panel minimum sizes as percentages; same length as defaultLayout. */
  minSizes: PersistedLayout;
};

function safeReadStorage(key: string): unknown {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch {
    // localStorage unavailable (private mode) OR JSON parse failed.
    return null;
  }
}

function safeWriteStorage(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage disabled — silently drop. Layout still
    // works in-session; user just doesn't get persistence.
  }
}

/**
 * Validate a stored layout and clamp percentages above each panel's min.
 *
 * Returns `null` when the stored value is the wrong shape, the wrong
 * length, contains non-finite numbers, or sums outside [99, 101]
 * (1% slop for floating-point round-trip). Callers fall back to
 * `defaultLayout`.
 */
function validateLayout(
  raw: unknown,
  expectedLen: number,
  minSizes: PersistedLayout,
): PersistedLayout | null {
  if (!Array.isArray(raw)) return null;
  if (raw.length !== expectedLen) return null;
  const nums: number[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const n = Number(raw[i]);
    if (!Number.isFinite(n) || n <= 0 || n >= 100) return null;
    nums.push(n);
  }
  const sum = nums.reduce((a, b) => a + b, 0);
  if (sum < 99 || sum > 101) return null;

  // Clamp each panel up to its min. If that pushes total past 100, take
  // the deficit only from panels with headroom above their min — pro-rata
  // by available headroom, so we never re-cross a min boundary.
  const clamped = nums.map((n, i) => Math.max(n, minSizes[i] ?? 0));
  const sumClamped = clamped.reduce((a, b) => a + b, 0);
  if (Math.abs(sumClamped - 100) < 0.01) return clamped;

  // Sum of mins exceeds 100 → caller's config is impossible; bail out so
  // the default layout kicks in.
  const sumMins = minSizes.reduce((a, b) => a + b, 0);
  if (sumMins >= 100) return null;

  const deficit = sumClamped - 100;
  const headrooms = clamped.map((n, i) => Math.max(0, n - (minSizes[i] ?? 0)));
  const totalHeadroom = headrooms.reduce((a, b) => a + b, 0);
  if (totalHeadroom <= 0) return null;

  const out = clamped.map(
    (n, i) => n - (deficit * headrooms[i]!) / totalHeadroom,
  );
  return out;
}

export type UsePersistedLayoutResult = {
  /** Current layout percentages (length === defaultLayout.length). */
  layout: PersistedLayout;
  /** Pass to react-resizable-panels' PanelGroup onLayout. */
  onLayout: (sizes: PersistedLayout) => void;
};

/**
 * Persist an N-panel layout to localStorage with a 250 ms debounce so a
 * 60 Hz drag doesn't write hundreds of times per second.
 *
 * - Corrupt or out-of-range stored values fall back to `defaultLayout`.
 * - Stored values below the current min are clamped on mount.
 * - SSR-safe: no localStorage read until the effect fires on the client.
 */
export function usePersistedLayout(opts: Options): UsePersistedLayoutResult {
  const { storageKey, defaultLayout, minSizes } = opts;

  // Capture initial layout once so test renders don't go through a null tick.
  const [layout, setLayout] = useState<PersistedLayout>(() => {
    const stored = safeReadStorage(storageKey);
    const validated = validateLayout(stored, defaultLayout.length, minSizes);
    return validated ?? defaultLayout;
  });

  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestLayoutRef = useRef<PersistedLayout>(layout);
  latestLayoutRef.current = layout;

  // Re-read storage whenever storageKey changes. The useState initializer
  // only runs on first mount, so a callsite that swaps storageKey at
  // runtime (e.g. AskPuntt's chat-canvas → chat-ticket → chat-project
  // variant switching) would otherwise keep the previous key's layout in
  // state and apply it as `defaultSize` for the new variant.
  const prevKeyRef = useRef(storageKey);
  // Keep stable refs to the consumer's defaultLayout/minSizes so we don't
  // need them in the effect's deps (callsites typically allocate fresh
  // arrays each render).
  const defaultLayoutRef = useRef(defaultLayout);
  defaultLayoutRef.current = defaultLayout;
  const minSizesRef = useRef(minSizes);
  minSizesRef.current = minSizes;

  useEffect(() => {
    if (prevKeyRef.current === storageKey) return;
    // Flush any pending write to the OLD key before switching.
    if (writeTimerRef.current) {
      clearTimeout(writeTimerRef.current);
      safeWriteStorage(prevKeyRef.current, latestLayoutRef.current);
      writeTimerRef.current = null;
    }
    prevKeyRef.current = storageKey;
    const stored = safeReadStorage(storageKey);
    const validated = validateLayout(
      stored,
      defaultLayoutRef.current.length,
      minSizesRef.current,
    );
    setLayout(validated ?? defaultLayoutRef.current);
  }, [storageKey]);

  const onLayout = useCallback(
    (sizes: PersistedLayout) => {
      // Always update state so the consumer's controlled PanelGroup sees
      // the latest values; debounce only the storage write.
      setLayout(sizes);
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
      writeTimerRef.current = setTimeout(() => {
        safeWriteStorage(storageKey, latestLayoutRef.current);
        writeTimerRef.current = null;
      }, DEBOUNCE_MS);
    },
    [storageKey],
  );

  // Flush any pending write on unmount so a route change mid-drag doesn't
  // lose the final ratio.
  useEffect(() => {
    return () => {
      if (writeTimerRef.current) {
        clearTimeout(writeTimerRef.current);
        safeWriteStorage(storageKey, latestLayoutRef.current);
      }
    };
  }, [storageKey]);

  return useMemo(() => ({ layout, onLayout }), [layout, onLayout]);
}
